/**
 * PATH: packages/ui/src/kanban/components/kanbanCardPresence.ts
 * FILE_ID: UI-KANBAN-CARD-PRESENCE-001
 * PURPOSE: Pure builder for the linked-card (⧉) chip tooltip — names the card's
 *          OTHER lane presences (home + placements) EXCLUDING the presence the
 *          tile currently represents, annotating deleted/archived boards.
 * WHY: BUG — the footer previously listed every placement regardless of which
 *      presence the tile was, so a tile ON "CA This Week" read "Also shown in:
 *      … › CA This Week" (itself). It also never listed the HOME board when the
 *      tile was a placement. This helper is unit-tested so the exclusion +
 *      degrade-gracefully rules can't silently regress.
 * RELATED:
 *   - ./KanbanCardFooter.tsx: sole consumer (renders the ⧉ chip).
 *   - ../types/card.ts: Card + CardLanePlacement contracts.
 */
import type { Card } from '../types'

/** The card fields this helper reads — kept narrow so tests pass minimal
 *  literals and the contract is obvious. */
export type PresenceCard = Pick<
  Card,
  | 'lane_placements'
  | 'placement_id'
  | 'home_board_name'
  | 'home_lane_name'
  | 'home_board_state'
>

function locationLabel(
  boardName: string | null | undefined,
  laneName: string | null | undefined,
): string | null {
  const board = boardName || null
  const lane = laneName || null
  if (board && lane) return `${board} › ${lane}`
  if (lane) return lane
  if (board) return board
  return null
}

function annotate(
  label: string,
  state: 'active' | 'archived' | 'deleted' | undefined,
): string {
  if (state === 'deleted') return `${label} (deleted board)`
  if (state === 'archived') return `${label} (archived board)`
  return label
}

/**
 * Named `Board › Lane` labels for every presence OTHER than the one the tile
 * currently represents. The tile is a placement when `card.placement_id` is
 * set (then that placement is excluded + the home presence IS listed);
 * otherwise the tile is the home presence (then home is excluded). Deleted /
 * archived boards are annotated. Pre-field payloads yield `[]` (no names),
 * which callers fall back to a bare count for.
 */
export function buildPresenceLabels(card: PresenceCard): string[] {
  const placements = card.lane_placements ?? []
  const currentPlacementId = card.placement_id ?? null
  const isHomeCurrent = currentPlacementId === null
  const labels: string[] = []

  if (!isHomeCurrent) {
    const homeLabel = locationLabel(card.home_board_name, card.home_lane_name)
    if (homeLabel) labels.push(annotate(homeLabel, card.home_board_state))
  }

  for (const p of placements) {
    if (currentPlacementId !== null && p.id === currentPlacementId) continue
    const label = locationLabel(p.board_name, p.lane_name)
    if (label) labels.push(annotate(label, p.board_state))
  }

  return labels
}

/**
 * The ⧉ chip's tooltip/aria title. Prefers the named-locations form; falls
 * back to the bare presence count when no location can be named (pre-field
 * payloads with no lane/board names on the wire).
 */
export function buildPresenceTitle(card: PresenceCard): string {
  const presenceCount = 1 + (card.lane_placements?.length ?? 0)
  const labels = buildPresenceLabels(card)
  if (labels.length > 0) {
    return `Also shown in: ${labels.join(', ')} — same card, edits reflect everywhere`
  }
  return `Shown in ${presenceCount} lanes — same card, edits reflect everywhere`
}
